import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { InfoBox } from '@gumtree/ui-library';
import DeliveryIntroDialog from '@gumtree/ui-library/src/delivery-intro-dialog/delivery-intro-dialog';
import { deliveryCss, SwitchDescription } from '../../styles';
import { BaseSyiResponse, dtlSyi } from '../../services/dtl-syi';
import type { SyiState } from '../../reducers';
import Loading from '../loading';
import ParcelSizeSelect from '../parcel-size-select';
import { updateSyi } from '../../reducers/syi';

export const SHIPPING_PANEL_ATTRIBUTES = ['support_shipping', 'parcel_size'];

export default function Shipping(props: Props) {
    const {
        context,
        expandCategory,
        isPriceEligible,
        firstCheckEligible,
        checkPriceLoading,
        showParcelSizeError,
    } = useSelector(
        ({
            syi: {
                context,
                expandCategory,
                isPriceEligible,
                firstCheckEligible,
                checkPriceLoading,
                showParcelSizeError,
            },
            userType,
        }: SyiState) => ({
            context,
            expandCategory,
            isPriceEligible,
            firstCheckEligible,
            checkPriceLoading,
            userType,
            showParcelSizeError,
        })
    );

    enum ParcelSize {
        SMALL = 'small',
        MEDIUM = 'medium',
        LARGE = 'large',
    }

    const parcelSizeTextMap = {
        [ParcelSize.SMALL]: 'Fits in a large envelope (e.g. t-shirt and under 1kg)',
        [ParcelSize.MEDIUM]: 'Fits in a shoebox (e.g. trainers and under 2kg)',
        [ParcelSize.LARGE]: 'Fits in a moving box (e.g. clothing bundle and under 5kg)',
    };

    const [showNewLabel, setShowNewLabel] = useState(false);
    const [supportShippingchanged, setSupportShippingchanged] = useState(false);

    useEffect(() => {
        const hasShown = localStorage.getItem('syiShippingNewLabelShown');
        if (!hasShown) {
            setShowNewLabel(true);
            localStorage.setItem('syiShippingNewLabelShown', '1');
        }
    }, []);

    const panel = props?.panelAttributes?.[props.panelName];
    const parcelSize = panel?.attributes?.parcel_size;
    const parcelSizeOptions = (parcelSize?.values ?? [])
        .filter((i) => i.value)
        .map((option) => ({
            value: option.value,
            title: option.displayValue,
            text: parcelSizeTextMap[option.value],
        }));

    const dispatch = useDispatch();
    // Whether it is a draft or an already published advertisement, the backend does not have the support_shipping parameter.
    // The draft status requires it to be enabled by default, while the editor requests it not to be enabled
    const initSupportShipping =
        context.postAdPageType === 'edit'
            ? props?.form?.attributes?.support_shipping === 'true'
            : props?.form?.attributes?.support_shipping !== 'false';
    const [supportShipping, setSupportShipping] = useState(initSupportShipping);
    const [selectedSize, setSelectedSize] = useState(props?.form?.attributes?.parcel_size);
    const [disabled, setDisabled] = useState(false);
    const [sizeChanged, setSizeChanged] = useState(false);
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

    useEffect(() => {
        // When the Form-element is unmounted and then hung again, the elements in the form may not be synchronized
        // resulting in the element.isValid() check failing
        // if (dtlSyi.form && typeof dtlSyi.form.loadElements === 'function') {
        //     dtlSyi.form.loadElements();
        // }
        if (!isPriceEligible) {
            setSupportShippingchanged(true);
            setSupportShipping(false);
            setDisabled(true);
        } else {
            if (!firstCheckEligible) {
                setSupportShippingchanged(true);
                setSupportShipping(true);
                setDisabled(false);
            }
        }
    }, [isPriceEligible]);

    useEffect(() => {
        if (supportShippingchanged) {
            dtlSyi.submitDraft();
            setSupportShippingchanged(false);
        }
    }, [supportShipping]);

    useEffect(() => {
        if (supportShipping && props.form.attributes.support_shipping !== 'false') {
            // The DOM state is unstable, and the requested data may undergo unexpected changes.
            // The deepSerializeForm function will merge them into one array if there are the same name in the SYI form.
            // Such as the appearance of postcode: ["TW91EL", "TW91EL"], instead of the postal code: "TW91EL"
            // So the call needs to be delayed.
            setTimeout(() => {
                dtlSyi.submitDraft();
            }, 0);
        }
    }, []);

    useEffect(() => {
        if (selectedSize) {
            dispatch(
                updateSyi({
                    hasSelectedParcelSize: true,
                })
            );
        }
    }, []);

    const handleSelectedParcelSize = (value: string) => {
        setSelectedSize(value);
        dispatch(updateSyi({ hasSelectedParcelSize: !!value }));
        dispatch(updateSyi({ showParcelSizeError: !value }));
        setSizeChanged(true);
    };

    useEffect(() => {
        if (selectedSize && sizeChanged) {
            dtlSyi.submitDraft();
        }
    }, [selectedSize]);

    return (
        <>
            <div
                className={cx('panel', {
                    'is-closed': expandCategory,
                })}
                css={deliveryCss}
                // data-toggler="channel:category-picker,selfBroadcast:false,className:is-closed,isOpen:false"
            >
                <h2 className="panel-title">
                    {showNewLabel && <div className="panel-title-label">new</div>}
                    <div className="panel-title-content">
                        <div>
                            <span className="panel-validation" /> Enable delivery
                        </div>
                        <div className="switch-box">
                            <SwitchDescription>{supportShipping ? 'Yes' : 'No'}</SwitchDescription>
                            <div className="form-element switch-container">
                                <label
                                    className={`switch ${supportShipping ? 'isChecked' : ''} ${
                                        disabled ? 'disabled' : ''
                                    }`}
                                    htmlFor="supportShipping"
                                >
                                    <input
                                        type="hidden"
                                        name="attributes[support_shipping]"
                                        defaultValue="false"
                                    />
                                    <input
                                        id="supportShipping"
                                        name="attributes[support_shipping]"
                                        disabled={disabled}
                                        type="checkbox"
                                        onChange={() => {
                                            setSupportShipping(!supportShipping);
                                        }}
                                        checked={supportShipping}
                                        defaultValue="true"
                                    />
                                    <span className="slider" />
                                </label>
                            </div>
                        </div>
                    </div>
                </h2>
                <div className="panel-content">
                    <div className={cx('delivery-content', { 'hide-visually': !isPriceEligible })}>
                        <div className="delivery-text">
                            <p>Sell faster by offering collection and delivery options.</p>
                            <p>
                                The buyer always covers the cost, while the sale is covered by Buyer
                                Protection.
                            </p>
                            <button
                                type="button"
                                className="txt-btn"
                                onClick={() => {
                                    setIsLearnMoreOpen(true);
                                }}
                            >
                                Learn more
                            </button>
                        </div>
                        <div
                            className={cx('select-container', {
                                'hide-visually': !supportShipping,
                            })}
                        >
                            <div className="select-label">Parcel size *</div>
                            <div className="form-element">
                                <ParcelSizeSelect
                                    disabled={!supportShipping}
                                    options={parcelSizeOptions}
                                    value={selectedSize}
                                    onChange={(v: string) => handleSelectedParcelSize(v)}
                                    placeholder="Select a parcel size"
                                />
                                <input
                                    disabled={!supportShipping}
                                    type="text"
                                    className="hide-visually"
                                    name="attributes[parcel_size]"
                                    id="parcelSize"
                                    value={selectedSize || undefined}
                                    data-validation-rules="isNotEmpty"
                                    required
                                    aria-required="true"
                                    autoComplete="off"
                                />

                                {showParcelSizeError && (
                                    <ul className="form-element-errors">
                                        <li> Please select parcel size</li>
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={cx('delivery-info-box', { 'hide-visually': isPriceEligible })}>
                        <InfoBox title="Item not eligible">
                            Delivery is not available for items less than £
                            {props.minPriceForSupportShipping} or over £
                            {props.maxPriceForSupportShipping}.
                            <button
                                type="button"
                                className="txt-btn"
                                onClick={() => {
                                    setIsLearnMoreOpen(true);
                                }}
                            >
                                Learn more
                            </button>
                        </InfoBox>
                    </div>
                </div>
                <DeliveryIntroDialog
                    isOpen={isLearnMoreOpen}
                    onClose={() => setIsLearnMoreOpen(false)}
                />
                {checkPriceLoading && <Loading />}
            </div>
        </>
    );
}

interface Props extends BaseSyiResponse {
    panelName: string;
    panelAttributes: {
        attributes: Record<string, { id: string }>;
    };
    minPriceForSupportShipping: string;
    maxPriceForSupportShipping: string;
}
